import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/layout/Topbar';
import AdminClimaraWaveB from '@/pages/admin/AdminClimaraWaveB';
import SuiteQuestionnaireBlock from '@/components/admin/SuiteQuestionnaireBlock';
import ClimaraCampaignConsole from '@/components/shared/ClimaraCampaignConsole';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  RefreshCw, ShieldCheck, TrendingUp,
  Plus, FileText, Building2, Radar, Rocket, Users, ClipboardList,
} from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';
import SuiteDocSection from '@/components/admin/SuiteDocSection';
import ClimaraNewCampaignDialog from '@/components/admin/ClimaraNewCampaignDialog';
import ClimaraReportView from '@/components/admin/ClimaraReportView';
import AdminClimaraSupervision from '@/pages/admin/AdminClimaraSupervision';
import SuiteOpsPanel from '@/components/admin/SuiteOpsPanel';

export default function AdminClimara() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useApp();
  const isHmAdmin = user?.role === 'hm_admin';
  const [companies, setCompanies] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [companyId, setCompanyId] = useState('');

  // Sprint CMA-B — campaign management state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  // Findings 2+3 (final UX) — canonical report workspace state
  const [reportCampaignId, setReportCampaignId] = useState(null);
  // Wave B UAT — lifecycle tabs + operational campaign console
  const [tab, setTab] = useState('setup');
  const [consoleCampaignId, setConsoleCampaignId] = useState(null);
  const [pendingRequests, setPendingRequests] = useState(0);
  // Item 8 (UAT iter 2) — campaign status filter
  const [campaignStatusFilter, setCampaignStatusFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const cos = await adminApi.listCompanies();
        setCompanies(cos);
        if (cos.length && !companyId) setCompanyId(cos[0].id);
      } catch {
        toast.error(t('climara.loadCompaniesError', { defaultValue: 'Failed to load companies' }));
      }
      adminApi.listConsultants().then(setConsultants).catch(() => {});
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sprint CMA-B — load campaigns for the selected company
  const loadCampaigns = async () => {
    if (!companyId) return;
    setCampaignsLoading(true);
    try {
      const list = await adminApi.climaraListCampaigns(companyId);
      setCampaigns(list || []);
      const pickDefault = (prev) => {
        if (prev && (list || []).some((c) => c.id === prev)) return prev;
        const firstActive = (list || []).find((c) => c.status === 'active');
        return firstActive?.id || (list || [])[0]?.id || null;
      };
      setConsoleCampaignId(pickDefault);
      setReportCampaignId(pickDefault);
    } catch (e) {
      console.warn('Failed to load Climara campaigns', e);
    } finally {
      setCampaignsLoading(false);
    }
  };
  useEffect(() => { loadCampaigns(); /* eslint-disable-next-line */ }, [companyId]);

  // Wave B UAT — pending campaign requests for this company (context strip badge)
  useEffect(() => {
    if (!companyId) return;
    adminApi.climaraCampaignRequests()
      .then((r) => setPendingRequests((r?.items || []).filter(
        (x) => x.company_id === companyId && x.status === 'pending').length))
      .catch(() => setPendingRequests(0));
  }, [companyId]);

  const company = useMemo(
    () => companies.find(c => c.id === companyId),
    [companies, companyId],
  );
  const activeCampaign = useMemo(
    () => campaigns.find((c) => c.status === 'active') || null,
    [campaigns],
  );
  const contextCampaign = activeCampaign || campaigns[0] || null;
  // Item 5 (UAT iter 2/3) — responsible HM Advisor for the selected client
  const advisor = useMemo(() => {
    const aid = company?.assigned_consultant_id;
    if (!aid) return null;
    return consultants.find((c) => c.id === aid) || null;
  }, [company, consultants]);
  const advisorName = advisor?.name || advisor?.email || null;
  // Item 8 — derived campaign status (expired = active past deadline)
  const derivedStatus = (c) => {
    if (c.status === 'active' && c.deadline && c.deadline < new Date().toISOString().slice(0, 10)) return 'expired';
    return c.status;
  };
  const filteredCampaigns = useMemo(
    () => campaigns.filter((c) => campaignStatusFilter === 'all' || derivedStatus(c) === campaignStatusFilter),
    [campaigns, campaignStatusFilter],
  );

  // Lifecycle stage for the context strip
  const stage = useMemo(() => {
    if (pendingRequests > 0) return { key: 'review', label: t('climara.stage.review', { defaultValue: 'Request review' }), color: 'bg-amber-100 text-amber-700' };
    if (activeCampaign) return { key: 'active', label: t('climara.stage.active', { defaultValue: 'Campaign active — monitoring' }), color: 'bg-sky-100 text-sky-700' };
    if (campaigns.some((c) => (c.metrics?.submitted || 0) > 0)) return { key: 'results', label: t('climara.stage.results', { defaultValue: 'Results & report' }), color: 'bg-emerald-100 text-emerald-700' };
    return { key: 'setup', label: t('climara.stage.setup', { defaultValue: 'Setup' }), color: 'bg-muted text-muted-foreground' };
  }, [pendingRequests, activeCampaign, campaigns, t]);

  return (
    <>
      <Topbar
        title="CLIMARA"
        subtitle={t('climara.subtitle', { defaultValue: 'Organizational climate — consulting workspace' })}
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56 h-9" data-testid="climara-company-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadCampaigns} disabled={campaignsLoading} data-testid="climara-refresh">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${campaignsLoading ? 'animate-spin' : ''}`} />
              {t('common.refresh', { defaultValue: 'Refresh' })}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        {/* Wave B UAT — active client context strip (always visible) */}
        <Card className="p-4 shadow-card border-l-4 border-l-primary" data-testid="climara-context-strip">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary shrink-0" />
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('climara.context.client', { defaultValue: 'Client' })}</p>
                <p className="text-sm font-bold" data-testid="climara-context-client">{company?.name || '—'}</p>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('climara.context.advisor', { defaultValue: 'HM Advisor' })}</p>
              {advisor ? (
                <button
                  type="button"
                  className={`flex items-center gap-2 mt-0.5 group ${isHmAdmin ? 'cursor-pointer' : 'cursor-default'}`}
                  onClick={() => { if (isHmAdmin) navigate(`/admin/advisors/${advisor.id}`); }}
                  title={isHmAdmin ? t('climara.context.openAdvisor', { defaultValue: 'Open advisor profile' }) : undefined}
                  data-testid="climara-context-advisor"
                >
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="text-xs bg-primary/10 text-primary font-bold">
                      {(advisor.name || advisor.email || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-left">
                    <span className={`block text-sm font-medium leading-tight ${isHmAdmin ? 'group-hover:underline' : ''}`}>{advisorName}</span>
                    {advisor.email && <span className="block text-xs text-muted-foreground leading-tight">{advisor.email}</span>}
                  </span>
                </button>
              ) : (
                <p className="text-sm font-medium" data-testid="climara-context-advisor">
                  {t('climara.context.noAdvisor', { defaultValue: 'Not assigned' })}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('climara.context.campaign', { defaultValue: 'Campaign' })}</p>
              <p className="text-sm font-medium" data-testid="climara-context-campaign">
                {contextCampaign ? `${contextCampaign.name} · ${contextCampaign.wave_label || contextCampaign.period || ''}` : t('climara.context.noCampaign', { defaultValue: 'No campaigns yet' })}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('climara.context.stage', { defaultValue: 'Stage' })}</p>
              <Badge className={`text-xs ${stage.color}`} data-testid="climara-context-stage">{stage.label}</Badge>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('climara.context.status', { defaultValue: 'Status' })}</p>
              {contextCampaign ? (
                <Badge variant={contextCampaign.status === 'active' ? 'default' : 'outline'} className="text-xs" data-testid="climara-context-status">
                  {contextCampaign.status === 'active'
                    ? t('climara.campaigns.statusActive', { defaultValue: 'Active' })
                    : t('climara.campaigns.statusClosed', { defaultValue: 'Closed' })}
                </Badge>
              ) : <span className="text-sm text-muted-foreground">—</span>}
            </div>
            {pendingRequests > 0 && (
              <Badge className="bg-amber-100 text-amber-700 text-xs ml-auto" data-testid="climara-context-pending">
                {t('climara.context.pendingRequests', { defaultValue: '{{n}} pending request(s)', n: pendingRequests })}
              </Badge>
            )}
          </div>
        </Card>

        {/* Founder UAT — role-aware operational supervision layer */}
        <SuiteOpsPanel
          suiteCode="climara"
          onKpiAction={(target) => setTab(target)}
          onClientOpen={(cid) => setCompanyId(cid)}
        />

        {/* Wave B UAT — lifecycle organization: Setup → Campaigns & Monitoring → Executive Report → Team Building */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex flex-wrap h-auto" data-testid="climara-lifecycle-tabs">
            {isHmAdmin && (
              <TabsTrigger value="supervision" data-testid="climara-tab-supervision">
                <TrendingUp className="h-3.5 w-3.5 mr-1.5" />
                {t('climara.tabs.supervision', { defaultValue: 'Supervision' })}
              </TabsTrigger>
            )}
            <TabsTrigger value="setup" data-testid="climara-tab-setup">
              <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
              {t('climara.tabs.setup', { defaultValue: '1. Setup' })}
              {pendingRequests > 0 && <Badge className="ml-1.5 bg-amber-100 text-amber-700 text-xs px-1">{pendingRequests}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="campaigns" data-testid="climara-tab-campaigns">
              <Radar className="h-3.5 w-3.5 mr-1.5" />
              {t('climara.tabs.campaigns', { defaultValue: '2. Campaigns & monitoring' })}
            </TabsTrigger>
            <TabsTrigger value="report" data-testid="climara-tab-report">
              <FileText className="h-3.5 w-3.5 mr-1.5" />
              {t('climara.tabs.report', { defaultValue: '3. Executive report' })}
            </TabsTrigger>
            <TabsTrigger value="teambuilding" data-testid="climara-tab-teambuilding">
              <Users className="h-3.5 w-3.5 mr-1.5" />
              {t('climara.tabs.teambuilding', { defaultValue: '4. Team Building' })}
            </TabsTrigger>
          </TabsList>

          {/* ---------------- 0. SUPERVISION (hm_admin) ---------------- */}
          {isHmAdmin && (
            <TabsContent value="supervision" className="mt-4">
              <AdminClimaraSupervision />
            </TabsContent>
          )}

          {/* ---------------- 1. SETUP ---------------- */}
          <TabsContent value="setup" className="space-y-6 mt-4">
            <AdminClimaraWaveB companyId={companyId} section="requests" />
            <SuiteQuestionnaireBlock suiteCode="climara" suiteName="Climara" />
            <Card className="p-4 shadow-card flex items-start gap-3 bg-emerald-50/40 border-emerald-200/60" data-testid="climara-anon-banner">
              <ShieldCheck className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold">{t('climara.anonymity', { defaultValue: 'Anonymity model' })}</p>
                <p className="text-xs text-muted-foreground">
                  {t('climara.anonymityDesc', { defaultValue: 'Participation tracking is operational (who responded), never confidential (what they answered). All results, analytics and PDFs are 100% anonymous and aggregated by department.' })}
                </p>
              </div>
            </Card>
          </TabsContent>

          {/* ---------------- 2. CAMPAIGNS & MONITORING ---------------- */}
          <TabsContent value="campaigns" className="space-y-6 mt-4">
            <Card className="p-6 shadow-card" data-testid="climara-campaigns-section">
              <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                <div>
                  <h3 className="font-display font-bold text-lg tracking-tight">
                    {t('climara.campaigns.title', { defaultValue: 'Campaigns' })}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t('climara.campaigns.sub', { defaultValue: 'Each campaign reuses a published Library questionnaire and the shared Assignment engine.' })}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={campaignStatusFilter} onValueChange={setCampaignStatusFilter}>
                    <SelectTrigger className="w-36 h-9 text-xs" data-testid="climara-campaign-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('climara.campaigns.filterAll', { defaultValue: 'All statuses' })}</SelectItem>
                      <SelectItem value="active">{t('climara.campaigns.statusActive', { defaultValue: 'Active' })}</SelectItem>
                      <SelectItem value="closed">{t('climara.campaigns.statusClosed', { defaultValue: 'Closed' })}</SelectItem>
                      <SelectItem value="expired">{t('climara.campaigns.statusExpired', { defaultValue: 'Expired' })}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={() => setCampaignDialogOpen(true)}
                    disabled={!companyId}
                    className="bg-gradient-primary hover:opacity-90"
                    data-testid="climara-new-campaign-btn"
                  >
                    <Plus className="h-4 w-4 mr-1.5" />
                    {t('climara.campaigns.newBtn', { defaultValue: 'New campaign' })}
                  </Button>
                </div>
              </div>

              {campaignsLoading && (
                <p className="text-sm text-muted-foreground" data-testid="climara-campaigns-loading">
                  {t('common.loading', { defaultValue: 'Loading…' })}
                </p>
              )}

              {!campaignsLoading && campaigns.length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground border border-dashed rounded-md" data-testid="climara-campaigns-empty">
                  {t('climara.campaigns.empty', { defaultValue: 'No campaigns yet for this company. Approve a client request or click \u201CNew campaign\u201D to start.' })}
                </div>
              )}

              {!campaignsLoading && campaigns.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                        <th className="py-2 pr-3">{t('climara.campaigns.colName', { defaultValue: 'Name' })}</th>
                        <th className="py-2 px-3">{t('climara.campaigns.colPeriod', { defaultValue: 'Period' })}</th>
                        <th className="py-2 px-3 text-center">{t('climara.campaigns.colInvited', { defaultValue: 'Invited' })}</th>
                        <th className="py-2 px-3 text-center">{t('climara.campaigns.colSubmitted', { defaultValue: 'Submitted' })}</th>
                        <th className="py-2 px-3 text-center">{t('climara.campaigns.colRate', { defaultValue: 'Rate' })}</th>
                        <th className="py-2 px-3">{t('climara.campaigns.colStatus', { defaultValue: 'Status' })}</th>
                        <th className="py-2 pl-3 text-right">{t('common.actions', { defaultValue: 'Actions' })}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCampaigns.length === 0 && (
                        <tr><td colSpan={7} className="py-5 text-center text-muted-foreground text-xs">
                          {t('climara.campaigns.noneForFilter', { defaultValue: 'No campaigns match this filter.' })}
                        </td></tr>
                      )}
                      {filteredCampaigns.map((c) => (
                        <tr key={c.id}
                            className={`border-b last:border-0 cursor-pointer ${consoleCampaignId === c.id ? 'bg-primary/5' : ''}`}
                            onClick={() => setConsoleCampaignId(c.id)}
                            data-testid={`climara-campaign-row-${c.id}`}>
                          <td className="py-3 pr-3 font-medium">{c.name}</td>
                          <td className="py-3 px-3 text-muted-foreground">{c.wave_label || c.period}</td>
                          <td className="py-3 px-3 text-center tabular-nums">{c.metrics?.invited ?? '—'}</td>
                          <td className="py-3 px-3 text-center tabular-nums">{c.metrics?.submitted ?? '—'}</td>
                          <td className="py-3 px-3 text-center tabular-nums font-bold">{c.metrics?.rate_pct ?? 0}%</td>
                          <td className="py-3 px-3">
                            <Badge variant={derivedStatus(c) === 'active' ? 'default' : 'outline'}
                                   className={`text-xs ${derivedStatus(c) === 'expired' ? 'text-rose-600 border-rose-300' : ''}`}>
                              {derivedStatus(c) === 'active'
                                ? t('climara.campaigns.statusActive', { defaultValue: 'Active' })
                                : derivedStatus(c) === 'expired'
                                  ? t('climara.campaigns.statusExpired', { defaultValue: 'Expired' })
                                  : t('climara.campaigns.statusClosed', { defaultValue: 'Closed' })}
                            </Badge>
                          </td>
                          <td className="py-3 pl-3 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="inline-flex items-center gap-1">
                              <Button
                                size="sm" variant={consoleCampaignId === c.id ? 'default' : 'ghost'}
                                onClick={() => {
                                  setConsoleCampaignId(c.id);
                                  // FIX 8 — the action must produce a visible result:
                                  // scroll straight to the campaign console.
                                  setTimeout(() => document.getElementById('climara-console-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
                                }}
                                data-testid={`climara-campaign-console-${c.id}`}
                              >
                                <Radar className="h-3.5 w-3.5 mr-1" />
                                {t('climara.campaigns.consoleBtn', { defaultValue: 'Manage campaign' })}
                              </Button>
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => { setReportCampaignId(c.id); setTab('report'); }}
                                data-testid={`climara-campaign-report-${c.id}`}
                              >
                                <FileText className="h-3.5 w-3.5 mr-1" />
                                {t('climara.campaigns.reportBtn', { defaultValue: 'Report' })}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Wave B UAT — operational Campaign Console */}
            {consoleCampaignId && (
              <Card className="p-6 shadow-card" data-testid="climara-console-card">
                <div className="flex items-center gap-2 mb-4" id="climara-console-anchor">
                  <Rocket className="h-5 w-5 text-primary" />
                  <h3 className="font-display font-bold text-lg tracking-tight">
                    {t('climara.console.title', { defaultValue: 'Campaign management' })}
                  </h3>
                </div>
                <ClimaraCampaignConsole
                  key={consoleCampaignId}
                  campaignId={consoleCampaignId}
                  mode="admin"
                  onChanged={loadCampaigns}
                  onOpenReport={() => { setReportCampaignId(consoleCampaignId); setTab('report'); }}
                />
              </Card>
            )}
          </TabsContent>

          {/* ---------------- 3. EXECUTIVE REPORT — canonical workspace (Findings 2+3) ---------------- */}
          <TabsContent value="report" className="space-y-6 mt-4">
            <Card className="p-6 shadow-card" data-testid="climara-report-workspace">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                <div>
                  <h3 className="font-display font-bold text-lg tracking-tight">
                    {t('climara.reportTab.title', { defaultValue: 'Executive climate report' })}
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-2xl">
                    {t('climara.reportTab.sub', { defaultValue: 'Single report workspace: generate, review, export and release the campaign report to the client.' })}
                  </p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    {t('climara.reportTab.selectCampaign', { defaultValue: 'Campaign' })}
                  </label>
                  <Select value={reportCampaignId || ''} onValueChange={setReportCampaignId}>
                    <SelectTrigger className="w-72 h-9 mt-0.5" data-testid="climara-report-campaign-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {campaigns.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name} · {c.wave_label || c.period || ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {!reportCampaignId ? (
                <div className="py-8 text-center text-sm text-muted-foreground border border-dashed rounded-md" data-testid="climara-report-no-campaign">
                  {t('climara.reportTab.noCampaign', { defaultValue: 'No campaigns yet for this company. Create a campaign first — the executive report is generated from campaign responses.' })}
                </div>
              ) : (
                <ClimaraReportView key={reportCampaignId} campaignId={reportCampaignId} onChanged={loadCampaigns} />
              )}
            </Card>
          </TabsContent>

          {/* ---------------- 4. TEAM BUILDING ---------------- */}
          <TabsContent value="teambuilding" className="space-y-6 mt-4">
            <AdminClimaraWaveB companyId={companyId} section="teambuilding" />
          </TabsContent>
        </Tabs>

        <ClimaraNewCampaignDialog
          open={campaignDialogOpen}
          onOpenChange={setCampaignDialogOpen}
          companyId={companyId}
          onCreated={() => loadCampaigns()}
        />
        <SuiteDocSection suiteCode="climara" />
      </div>
    </>
  );
}
